<script setup lang="ts">
  import type { ISbStoryData } from 'storyblok'
  import type { ScrollingBannerStoryblok, ScrollingBannerItemStoryblok } from '@/storyblok/component-types-sb'
  import { toAssetImageProps } from '@/utils/storyblok/normalizers'
  import { marked } from 'marked'
  import type { TBannerItem } from '@/storyblok/types'

  const props = defineProps<{
    blok: ScrollingBannerStoryblok
  }>()

  const bannerItems = computed(() =>
    (props.blok.body ?? []).flatMap((item): TBannerItem[] => {
      if (typeof item === 'string') return []
      const storyItem = item as ISbStoryData<ScrollingBannerItemStoryblok>

      if (storyItem.content?.items?.length) {
        return storyItem.content.items.map((contentItem) => ({
          type: 'content' as const,
          iconProps: toAssetImageProps(contentItem.icon),
          html: marked(contentItem.text) as string
        }))
      }

      return (storyItem.content?.body ?? []).map((content) => ({
        type: 'legacy' as const,
        content
      }))
    })
  )

  const hasBannerItems = computed(() => bannerItems.value.length > 0)
  const firstSequenceRef = ref<HTMLElement | null>(null)
  const marqueeDistance = ref(0)

  const trackStyle = computed(() => ({
    '--marquee-distance': `${marqueeDistance.value}px`,
    '--marquee-duration': '60s'
  }))

  const shouldDuplicate = computed(() => bannerItems.value.length > 0)
  const canAnimate = computed(() => marqueeDistance.value > 0 && shouldDuplicate.value)

  let resizeObserver: ResizeObserver | null = null

  const updateMarqueeDistance = () => {
    marqueeDistance.value = firstSequenceRef.value?.scrollWidth ?? 0
  }

  onMounted(() => {
    nextTick(updateMarqueeDistance)

    if (firstSequenceRef.value && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateMarqueeDistance)
      resizeObserver.observe(firstSequenceRef.value)
    }
  })

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
  })
</script>

<template>
  <div
    v-if="blok.enabled && hasBannerItems"
    class="mt-4 rounded-sm bg-bt-blue-dark px-4 text-white text-body-3 sm:text-subtitle-1 md:mt-6"
  >
    <div class="group w-full overflow-hidden py-4">
      <div
        class="marquee-track flex w-max will-change-transform group-hover:[animation-play-state:paused]"
        :class="{ 'marquee-track--active': canAnimate }"
        :style="trackStyle"
      >
        <div
          ref="firstSequenceRef"
          class="marquee-sequence flex w-max gap-[33vw] pl-[33vw]"
        >
          <ScrollingBannerItems
            :banner-items="bannerItems"
            key-number="1"
          />
        </div>
        <div
          v-if="shouldDuplicate"
          class="marquee-sequence flex w-max gap-[33vw] pl-[33vw]"
          aria-hidden="true"
        >
          <ScrollingBannerItems
            :banner-items="bannerItems"
            key-number="2"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  @keyframes marquee-track {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(calc(-1 * var(--marquee-distance, 0px)));
    }
  }

  .marquee-track--active {
    animation: marquee-track var(--marquee-duration, 60s) linear infinite;
  }
</style>
